import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../services/api';
import {
  Box,
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Divider
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon
} from '@mui/icons-material';

function AddChild() {
  const navigate = useNavigate();
  const location = useLocation();
  const requestedReturnTo = location.state?.returnTo;
  const returnTo = typeof requestedReturnTo === 'string' && /^\/programs\/[^/]+\/enroll$/.test(requestedReturnTo)
    ? requestedReturnTo
    : '/my-account';
  const returningToEnrollment = returnTo !== '/my-account';
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    gender: '',
    grade: '',
    allergies: '',
    medical_conditions: '',
    additional_notes: '',
    waiver_signed: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const submitData = {
      ...formData,
      // The backend always assigns the safe child role.
      grade: parseInt(formData.grade, 10),
      date_of_birth: formData.date_of_birth ? `${formData.date_of_birth}T12:00:00Z` : ''
    };

    try {
      await api.addChild(submitData);
      navigate(returnTo, { replace: true, state: { childAdded: true } });
    } catch (error) {
      console.error('Add child failed:', error);
      setError(error.response?.data?.error || error.response?.data?.message || 'Failed to add child. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(returnTo);
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="md">
        <Button
          onClick={handleCancel}
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 3 }}
        >
          {returningToEnrollment ? 'Back to Enrollment' : 'Back to My Account'}
        </Button>

        <Card>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
              Add Child
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Enter your child's information below to add them to your account.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              {/* Basic Information Section */}
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Basic Information
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  placeholder="Enter child's first name"
                />

                <TextField
                  fullWidth
                  label="Last Name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  placeholder="Enter child's last name"
                />
              </Box>

              <TextField
                fullWidth
                label="Date of Birth"
                name="date_of_birth"
                type="date"
                value={formData.date_of_birth}
                onChange={handleChange}
                required
                disabled={isLoading}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: new Date().toISOString().split('T')[0] }}
                sx={{ mb: 2 }}
              />

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
                <FormControl fullWidth required>
                  <InputLabel>Gender</InputLabel>
                  <Select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    disabled={isLoading}
                    label="Gender"
                  >
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth required>
                  <InputLabel>Grade</InputLabel>
                  <Select
                    name="grade"
                    value={formData.grade}
                    onChange={handleChange}
                    disabled={isLoading}
                    label="Grade"
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((grade) => (
                      <MenuItem key={grade} value={grade}>
                        {grade}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Medical Information Section */}
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Medical Information
              </Typography>

              <TextField
                fullWidth
                label="Allergies"
                name="allergies"
                value={formData.allergies}
                onChange={handleChange}
                disabled={isLoading}
                placeholder="List any allergies (optional)"
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Medical Conditions"
                name="medical_conditions"
                value={formData.medical_conditions}
                onChange={handleChange}
                disabled={isLoading}
                placeholder="List any medical conditions (optional)"
                sx={{ mb: 2 }}
              />

              <Divider sx={{ my: 3 }} />

              {/* Additional Information Section */}
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Additional Information
              </Typography>

              <TextField
                fullWidth
                label="Additional Notes"
                name="additional_notes"
                value={formData.additional_notes}
                onChange={handleChange}
                disabled={isLoading}
                placeholder="Any additional information we should know (optional)"
                multiline
                rows={4}
                sx={{ mb: 3 }}
              />

              <Divider sx={{ my: 3 }} />

              {/* Waiver Section */}
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Participant Waiver
              </Typography>

              <Alert severity="info" sx={{ mb: 2 }}>
                Please read the waiver below carefully and check the box at the bottom to agree.
              </Alert>

              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  mb: 3,
                  maxHeight: '400px',
                  overflow: 'auto',
                  bgcolor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'grey.300',
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
                  Merrimack Valley Youth Program, Inc.
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
                  Participant Waiver, Release of Liability, Media Consent, and Medical Authorization
                </Typography>

                <Typography variant="body2" paragraph>
                  <strong>Organization:</strong> Merrimack Valley Youth Program, Inc.<br />
                  <strong>Location:</strong> Massachusetts<br />
                  <strong>Participant:</strong> Minor child enrolled in MVYP programs<br />
                  <strong>Parent/Guardian:</strong> Individual completing this form
                </Typography>

                <Typography variant="body2" paragraph>
                  By enrolling this participant in any program, event, class, or activity organized, hosted, or sponsored by Merrimack Valley Youth Program, Inc. ("MVYP"), whether in person or online, I acknowledge and agree to the following terms:
                </Typography>

                <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
                  1. Program Participation & Assumption of Risk
                </Typography>
                <Typography variant="body2" paragraph>
                  I understand that MVYP programs may include, but are not limited to, educational activities, religious instruction, community service, recreational activities, and physical activities such as sports, games, and outdoor play.
                </Typography>
                <Typography variant="body2" paragraph>
                  I acknowledge that participation in these activities involves inherent risks, including, but not limited to, minor injuries (e.g., scrapes, bruises, or sprains), more serious injuries, or other unforeseen incidents. I understand that these risks may arise from physical activity, use of facilities or equipment, interactions with other participants, or environmental conditions.
                </Typography>
                <Typography variant="body2" paragraph>
                  I voluntarily assume all reasonable risks associated with my participant's participation and confirm that my participant is physically and emotionally able to participate in MVYP programs.
                </Typography>

                <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
                  2. Release and Limitation of Liability
                </Typography>
                <Typography variant="body2" paragraph>
                  To the fullest extent permitted by Massachusetts law, I hereby release, waive, and discharge MVYP, its directors, officers, employees, volunteers, instructors, and agents from any and all claims, demands, actions, or causes of action arising out of or related to my participant's participation in MVYP programs, including but not limited to claims for personal injury, illness, property damage, or loss. In consideration of permitting my participant to participate in these activities, I hereby take action for my participant, their executors, administrators, heirs, next of kin, successors, and assigns as follows: (i) I hereby waive, release, and discharge from any and all liability, including but not limited to, liability arising from the negligence or fault of MVYP, its directors, officers, employees, volunteers, instructors, and agents, for death, disability, personal injury, property damage, property theft, or actions of any kind which may hereafter occur to my participant; (b) I hereby indemnify, hold harmless, and promise not to sue MVYP, its directors, officers, employees, volunteers, instructors, and agents from any and all liabilities or claims made as a result of participation in these activities, whether caused by the negligence or otherwise. This release applies except in cases of gross negligence or willful misconduct by MVYP.
                </Typography>

                <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
                  3. Medical Information & Emergency Medical Consent
                </Typography>
                <Typography variant="body2" paragraph>
                  I certify that I have disclosed any relevant medical conditions, allergies, medications, or special needs during the enrollment process, or I understand that I am responsible for updating MVYP if such information changes. In the event of an illness or injury where I cannot be reached in a timely manner, I hereby authorize MVYP staff or volunteers to:
                </Typography>
                <Typography variant="body2" paragraph sx={{ pl: 2 }}>
                  • Administer basic first aid, and/or<br />
                  • Seek emergency medical treatment, including transportation by ambulance if deemed necessary
                </Typography>
                <Typography variant="body2" paragraph>
                  I understand that MVYP does not provide medical insurance for participants and that any medical expenses incurred are my responsibility.
                </Typography>

                <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
                  4. Photo, Video, and Media Release
                </Typography>
                <Typography variant="body2" paragraph>
                  I grant permission to MVYP to photograph, record, or otherwise capture my participant's image, likeness, voice, or participation during programs or events. I understand and agree that these photos or videos may be used by MVYP for lawful purposes, including but not limited to:
                </Typography>
                <Typography variant="body2" paragraph sx={{ pl: 2 }}>
                  • Website content<br />
                  • Social media<br />
                  • Promotional materials<br />
                  • Educational or informational publications<br />
                  • Marketing and fundraising efforts
                </Typography>
                <Typography variant="body2" paragraph>
                  I acknowledge that these materials may be used without compensation and that MVYP will make reasonable efforts to use such media respectfully and in a manner consistent with community values and standards.
                </Typography>

                <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
                  5. Code of Conduct & Dismissal
                </Typography>
                <Typography variant="body2" paragraph>
                  I understand that MVYP seeks to maintain a safe, respectful, and faith-centered environment. I agree that my participant is expected to follow program rules, demonstrate respectful behavior, and comply with staff instructions. MVYP reserves the right to remove or suspend a participant from a program if their behavior poses a safety risk or significantly disrupts the program environment. In such cases, no refund or compensation is guaranteed.
                </Typography>

                <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
                  6. Non-Discrimination & Inclusivity
                </Typography>
                <Typography variant="body2" paragraph>
                  MVYP is committed to fostering an inclusive and welcoming environment. Participation in programs is not contingent on race, ethnicity, gender, disability, or socioeconomic status, and accommodations may be requested where reasonable.
                </Typography>

                <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
                  7. Acknowledgment & Agreement
                </Typography>
                <Typography variant="body2" paragraph>
                  By completing the enrollment process and submitting this waiver electronically, I confirm that:
                </Typography>
                <Typography variant="body2" paragraph>
                  I represent that I am the parent, legal guardian, and/or otherwise authorized to sign on behalf of the minor(s) and have the legal capacity to execute this agreement on behalf of the minor(s) participating in MYVP's activities. I will be held responsible for any violations made by the participant. I have read and understood this waiver in its entirety. I agree to its terms knowingly and voluntarily. I understand that this waiver is binding to the fullest extent allowed by law.
                </Typography>
              </Paper>

              <FormControlLabel
                control={
                  <Checkbox
                    name="waiver_signed"
                    checked={formData.waiver_signed}
                    onChange={handleCheckboxChange}
                    disabled={isLoading}
                    required
                  />
                }
                label={
                  <Typography variant="body2">
                    <strong>I have read and agree to the above waiver, release of liability, media consent, and medical authorization on behalf of my child.</strong>
                  </Typography>
                }
                sx={{ mb: 4 }}
              />

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={
                    isLoading ||
                    !formData.first_name.trim() ||
                    !formData.last_name.trim() ||
                    !formData.date_of_birth ||
                    !formData.gender ||
                    !formData.grade ||
                    !formData.waiver_signed
                  }
                  startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                >
                  {isLoading ? 'Adding Child...' : 'Add Child'}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}

export default AddChild;
